import { AnalyticsBrowser } from '@segment/analytics-next';
import type {
  AnalyticsEnvironment,
  AnalyticsEventName,
  AnalyticsEventProperties,
  AnalyticsProduct,
} from 'services/types/analytics';
import { ANALYTICS_EVENT_NAMES } from 'services/types/analytics';

// Create the ANALYTICS_EVENT object by mapping the array
export const ANALYTICS_EVENT: Record<AnalyticsEventName, string> =
  Object.fromEntries(
    ANALYTICS_EVENT_NAMES.map((name) => [name, name]),
  ) as Record<AnalyticsEventName, string>;

const ENVIRONMENT_MAP: { [key: string]: AnalyticsEnvironment } = {
  development: 'dev',
  production: 'prod',
  qa: 'qa',
  stage: 'stage',
  preprod: 'preprod',
  mobile: 'mobile',
  test: 'test',
  local: 'local',
} as const;

// Store the original fetch function; override with custom wrapper
const originalFetch = window.fetch;

window.fetch = async function (input, init) {
  // Return as-is if we're not dealing with analytics-api
  if (!`${input}`.includes('analytics-api')) return originalFetch(input, init);

  // Ensure init is defined and headers are correctly manipulated
  const modifiedInit = init ? { ...init } : {};
  const headers = new Headers(modifiedInit.headers);
  headers.set('Content-Type', 'application/json');
  headers.set('x-api-key', `${process.env.REACT_APP_ANALYTICS_API_KEY}`);

  // Attach the modified headers back to init
  modifiedInit.headers = headers;

  // Call the original fetch with modified arguments
  return originalFetch(input, modifiedInit);
};

/**
 * Service for handling analytics tracking within the application.
 *
 * @class AnalyticsService
 * @property {AnalyticsBrowser} _analyticsClient - Client instance for sending analytics data.
 * @property {Object.<string, AnalyticsEventName>} _events - Mapping of event keys to event names.
 *
 * @constructor
 * @param {AnalyticsEnvironment} _environment - The environment in which the application is running.
 * @param {AnalyticsProduct} _product - The product identifier.
 * @param {string} _appId - The application identifier.
 *
 * @method get events - Retrieves the mapping of event keys to event names.
 * @returns {Object.<string, AnalyticsEventName>} The event mapping.
 *
 * @method track - Tracks an analytics event.
 * @param {AnalyticsEventName} name - The name of the event to track.
 * @param {AnalyticsEventProperties} - The properties of the event to track.
 * @returns {void}
 */
export class AnalyticsService {
  private readonly _analyticsClient: AnalyticsBrowser;

  public constructor(
    private readonly _environment: AnalyticsEnvironment,
    private readonly _product: AnalyticsProduct,
    private readonly _appId: string,
    private readonly _client: string,
    private readonly _appVersion: string,
  ) {
    // Gozio common attributes to be sent with every request
    const gozioAttrs = {
      appId: this._appId,
      client: this._client,
      product: this._product,
      appVersion: this._appVersion,
      env: ENVIRONMENT_MAP[this._environment || 'development'],
    };

    // Point to our own api endpoint
    this._analyticsClient = AnalyticsBrowser.load({
      writeKey: 'placeholder',
      cdnSettings: {
        integrations: {
          'Segment.io': {
            apiHost: process.env.REACT_APP_ANALYTICS_HOST,
            protocol: 'https',
          },
        },
      },
    });

    // Middleware to add common attributes
    this._analyticsClient.addSourceMiddleware(({ payload, next }) => {
      payload.obj.context ??= {};
      payload.obj.context.gozio = gozioAttrs;
      next(payload);
    });
  }

  private readonly _events: Record<AnalyticsEventName, string> =
    ANALYTICS_EVENT;

  /**
   * Getter for the analytics events.
   *
   * @returns {Object} An object where the keys are event names and the values are of type `AnalyticsEventName`.
   */
  get events(): Record<AnalyticsEventName, string> {
    return this._events;
  }

  /**
   * Tracks an analytics event with the specified name and properties.
   *
   * @param name - The name of the analytics event to track.
   * @param properties - The properties of the analytics event, which include
   *                     specific event properties and optional common properties.
   *
   * @throws Will log an error if there is an issue tracking the event.
   */
  track = (name: string, properties: AnalyticsEventProperties = {}) => {
    try {
      this._analyticsClient.track(name, { ...properties });
    } catch (error) {
      console.error(
        `Error tracking event [${name}]: ${JSON.stringify(properties)}:`,
        error,
      );
    }
  };
}

let _analyticsServiceInstance: AnalyticsService;

/**
 * Retrieves the singleton instance of the AnalyticsService.
 *
 * @returns {AnalyticsService} The singleton instance of the AnalyticsService.
 */
export const getAnalyticsService = (): AnalyticsService =>
  _analyticsServiceInstance;

/**
 * Factory function to create an instance of `AnalyticsService`.
 *
 * @param environment - The analytics environment.
 * @param product - The analytics product (i.e. 'kiosk' or 'web').
 * @param networkName - The lower case network name.
 * @returns An instance of `AnalyticsService`.
 */
export default function AnalyticsServiceFactory(
  environment: AnalyticsEnvironment,
  product: AnalyticsProduct,
  networkName: string,
  kioskId: string,
): AnalyticsService {
  if (!_analyticsServiceInstance) {
    const isKiosk = product === 'kiosk';
    const appId =
      isKiosk && kioskId ? kioskId : process.env.REACT_APP_WEB_APP_ID || '';
    const client = `janus${product}`.toLowerCase();
    const appVersion = process.env.REACT_APP_VERSION;

    _analyticsServiceInstance = new AnalyticsService(
      environment,
      product,
      appId,
      client,
      appVersion ?? '',
    );
  }
  return _analyticsServiceInstance;
}
